import React, { forwardRef } from 'react';

export type TextAreaProps = {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  name?: string;
  rows?: number;
  maxLength?: number;
  fullWidth?: boolean;
};

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      placeholder,
      value,
      onChange,
      error,
      disabled = false,
      required = false,
      className = '',
      id,
      name,
      rows = 4,
      maxLength,
      fullWidth = true,
    },
    ref
  ) => {
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    
    return (
      <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
        {label && (
          <label 
            htmlFor={textareaId} 
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}
        
        <textarea
          ref={ref}
          id={textareaId}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          className={`
            flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm 
            ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium 
            placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 
            focus-visible:ring-primary-500 focus-visible:ring-offset-2 
            disabled:cursor-not-allowed disabled:opacity-50 
            dark:border-gray-700 dark:bg-gray-900 dark:ring-offset-gray-950 
            dark:placeholder:text-gray-400 dark:focus-visible:ring-primary-400
            resize-y min-h-[100px] max-h-[500px]
            ${error ? 'border-error-500 focus-visible:ring-error-500' : ''}
            ${disabled ? 'bg-gray-100 dark:bg-gray-800' : ''}
          `}
        />
        
        {error && (
          <p className="mt-1 text-sm text-error-500">{error}</p>
        )}
        
        {maxLength && value && (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
            {value.length}/{maxLength} caractères
          </div>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;